import React, { useState, useEffect } from 'react';
import { BookOpen, Edit3, Save, X, Plus, Trash2 } from 'lucide-react';
import { getReadingList, saveReadingList } from '../../services/homepageContentService';

const ReadingListCard = ({ isAdmin, setMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readingList, setReadingList] = useState([]);

  const [newItem, setNewItem] = useState({
    title: '',
    author: '',
    category: '',
    status: 'Want to Read',
    description: ''
  });

  useEffect(() => {
    const loadReadingList = async () => {
      try {
        const data = await getReadingList();
        if (data) {
          setReadingList(data);
        }
      } catch (error) {
        console.error('Error loading reading list:', error);
      } finally {
        setLoading(false);
      }
    };
    loadReadingList();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveReadingList(readingList);
      setMessage("Reading list updated successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving reading list:', error);
      setMessage("Failed to save reading list. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleAddItem = () => {
    if (newItem.title && newItem.author) {
      setReadingList(prev => [...prev, { ...newItem, id: `temp-${Date.now()}` }]);
      setNewItem({ title: '', author: '', category: '', status: 'Want to Read', description: '' });
    }
  };

  const handleDeleteItem = (id) => {
    setReadingList(prev => prev.filter(item => item.id !== id));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Reading': return 'bg-blue-100 text-blue-800';
      case 'Want to Read': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !isEditing) {
    return (
      <div className="p-6 panel">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="p-6 panel">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 mb-4 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-stone-600" />
          Reading List
        </h3>
        <div className="space-y-4">
          {readingList.map(item => (
            <div key={item.id} className="p-3 bg-paper rounded-xl border border-line">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-1">by {item.author}</p>
              {item.category && (
                <p className="text-xs text-indigo-600 mb-2">{item.category}</p>
              )}
              {item.description && (
                <p className="text-xs text-gray-500">{item.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 panel">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-stone-600" />
          Reading List
        </h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Add New Item Form */}
          <div className="p-4 bg-paper rounded-xl border border-stone-200">
            <h4 className="font-semibold text-indigo-800 mb-3 flex items-center">
              <Plus className="w-4 h-4 mr-1" />
              Add New Item
            </h4>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                placeholder="Book Title"
                value={newItem.title}
                onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                className="field"
              />
              <input
                type="text"
                placeholder="Author"
                value={newItem.author}
                onChange={(e) => setNewItem(prev => ({ ...prev, author: e.target.value }))}
                className="field"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                placeholder="Category"
                value={newItem.category}
                onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                className="field"
              />
              <select
                value={newItem.status}
                onChange={(e) => setNewItem(prev => ({ ...prev, status: e.target.value }))}
                className="field"
              >
                <option value="Want to Read">Want to Read</option>
                <option value="Reading">Reading</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            <textarea
              placeholder="Description (optional)"
              value={newItem.description}
              onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
              rows="2"
              className="field mb-3"
            />
            <button
              onClick={handleAddItem}
              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition"
            >
              Add Item
            </button>
          </div>

          {/* Existing Items */}
          <div className="space-y-3">
            {readingList.map(item => (
              <div key={item.id} className="p-3 bg-paper rounded-xl border border-line">
                <div className="flex justify-between items-start mb-2">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => setReadingList(prev => prev.map(i => i.id === item.id ? { ...i, title: e.target.value } : i))}
                    className="font-semibold text-gray-900 text-sm bg-transparent border-none p-0 focus:ring-0"
                  />
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="p-1 text-red-500 hover:text-red-700 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={item.author}
                    onChange={(e) => setReadingList(prev => prev.map(i => i.id === item.id ? { ...i, author: e.target.value } : i))}
                    className="text-xs text-gray-600 bg-transparent border-none p-0 focus:ring-0"
                    placeholder="Author"
                  />
                  <select
                    value={item.status}
                    onChange={(e) => setReadingList(prev => prev.map(i => i.id === item.id ? { ...i, status: e.target.value } : i))}
                    className="field text-xs py-1"
                  >
                    <option value="Want to Read">Want to Read</option>
                    <option value="Reading">Reading</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => setReadingList(prev => prev.map(i => i.id === item.id ? { ...i, category: e.target.value } : i))}
                  className="text-xs text-indigo-600 bg-transparent border-none p-0 focus:ring-0 mb-1"
                  placeholder="Category"
                />
                <textarea
                  value={item.description}
                  onChange={(e) => setReadingList(prev => prev.map(i => i.id === item.id ? { ...i, description: e.target.value } : i))}
                  className="text-xs text-gray-500 bg-transparent border-none p-0 focus:ring-0 w-full"
                  placeholder="Description"
                  rows="2"
                />
              </div>
            ))}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {readingList.map(item => (
            <div key={item.id} className="p-3 bg-paper rounded-xl border border-line">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-1">by {item.author}</p>
              {item.category && (
                <p className="text-xs text-indigo-600 mb-2">{item.category}</p>
              )}
              {item.description && (
                <p className="text-xs text-gray-500">{item.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReadingListCard;
